import { CheckOutlined, DownOutlined, LockOutlined } from "@ant-design/icons"
import { Select, Tooltip, Typography } from "antd"
import React from "react"

import {
  LIFECYCLE_STAGE_LABELS,
  LIFECYCLE_STAGE_VALUES,
  lifecycleStageIndex
} from "./lead-lifecycle-stages.ts"
import type { RequirementDraft } from "./useLeadLifecycleRules.ts"
import type { LeadLifecycleRequirement } from "./lead-lifecycle.types.ts"

const { Text } = Typography

interface RequirementRowProps {
  requirement: LeadLifecycleRequirement
  draft: RequirementDraft
  expanded: boolean
  onToggleExpanded: () => void
  onStageChange: (stage: number | null) => void
  onFieldsChange: (fields: string[]) => void
}

type CellState = "selected" | "inherited" | "off"

const cellClasses: Record<CellState, string> = {
  selected: "bg-blue-600 text-white border-blue-600",
  inherited: "bg-blue-50 text-blue-700 border-blue-200",
  off: "bg-white text-gray-400 border-gray-200 hover:border-gray-300"
}

export function RequirementRow({
  requirement,
  draft,
  expanded,
  onToggleExpanded,
  onStageChange,
  onFieldsChange
}: RequirementRowProps) {
  const activeIndex = lifecycleStageIndex(draft.from_stage)
  const locked = requirement.locked

  const cellState = (index: number): CellState => {
    if (activeIndex === -1) return "off"
    if (index === activeIndex) return "selected"
    if (index > activeIndex) return "inherited"
    return "off"
  }

  return (
    <div className="border-b border-gray-100 py-3 last:border-0">
      <div className="flex flex-wrap items-center gap-3">
        <div className="flex min-w-[220px] flex-1 items-center gap-2">
          {requirement.supports_specific && (
            <button
              aria-label={expanded ? "Collapse" : "Expand"}
              className="flex h-5 w-5 items-center justify-center text-gray-400 hover:text-gray-600"
              onClick={onToggleExpanded}
              type="button"
            >
              <DownOutlined
                className={`text-[10px] transition-transform ${expanded ? "rotate-180" : ""}`}
              />
            </button>
          )}
          <Text className={requirement.supports_specific ? "" : "ml-7"}>
            {requirement.label}
          </Text>
          {locked && (
            <Tooltip title="Always required from this stage onward — this cannot be turned off.">
              <LockOutlined className="text-gray-400" />
            </Tooltip>
          )}
        </div>

        <div
          className="flex flex-wrap gap-1"
          role="radiogroup"
          aria-label={`Applies from — ${requirement.label}`}
        >
          <button
            aria-checked={draft.from_stage === null}
            className={`rounded-full border px-3 py-1 text-xs font-medium transition-colors ${
              draft.from_stage === null ? cellClasses.selected : cellClasses.off
            } ${locked ? "cursor-not-allowed opacity-60" : ""}`}
            disabled={locked}
            onClick={() => onStageChange(null)}
            role="radio"
            type="button"
          >
            Never
          </button>
          {LIFECYCLE_STAGE_VALUES.map((stage, index) => {
            const state = cellState(index)
            return (
              <Tooltip
                key={stage}
                title={
                  state === "inherited"
                    ? `Required from ${LIFECYCLE_STAGE_LABELS[LIFECYCLE_STAGE_VALUES[activeIndex]]} onward`
                    : undefined
                }
              >
                <button
                  aria-checked={state === "selected"}
                  className={`flex items-center gap-1 rounded-full border px-3 py-1 text-xs font-medium transition-colors ${cellClasses[state]} ${
                    locked ? "cursor-not-allowed opacity-60" : ""
                  }`}
                  disabled={locked}
                  onClick={() => onStageChange(stage)}
                  role="radio"
                  type="button"
                >
                  {state !== "off" && <CheckOutlined className="text-[10px]" />}
                  {LIFECYCLE_STAGE_LABELS[stage]}
                </button>
              </Tooltip>
            )
          })}
        </div>
      </div>

      {expanded && requirement.supports_specific && (
        <div className="ml-7 mt-3 flex flex-col gap-1 rounded-md bg-gray-50 p-3">
          <Text className="text-xs" type="secondary">
            Required fields
          </Text>
          <Select
            allowClear
            className="max-w-md"
            mode="multiple"
            onChange={(fields) => onFieldsChange(fields)}
            options={requirement.fields.map((field) => ({
              label: field.label,
              value: field.key
            }))}
            placeholder="Uses the standard built-in rule"
            value={draft.selected_fields ?? []}
          />
          <Text className="text-xs" type="secondary">
            Leave empty to use EZBuild&apos;s standard built-in rule for this
            requirement.
          </Text>
        </div>
      )}
    </div>
  )
}
